import { create } from 'zustand';
import {
  clearBaseline as clearProjectBaseline,
  computeTaskScheduleEdit,
  createTaskCustomField as createProjectTaskCustomField,
  customFieldId,
  deleteTaskCustomField as deleteProjectTaskCustomField,
  fixtures,
  minutes,
  withWbsCodes,
  rollUpSummaryProgress,
  recordActualWork as recordProjectActualWork,
  setTaskProgress as setProjectTaskProgress,
  setTaskProgressMode as setProjectTaskProgressMode,
  setTaskWorkflowStatus as setProjectTaskWorkflowStatus,
  renameTaskCustomField as renameProjectTaskCustomField,
  setTaskCustomFieldValue as setProjectTaskCustomFieldValue,
  type Assignment,
  type BudgetAssignment,
  type BaselineNumber,
  type CalendarException,
  type CalendarId,
  type CustomFieldDefinition,
  type CustomFieldId,
  type CustomFieldValue,
  type Dependency,
  type DependencyType,
  type ISODateTime,
  type Project,
  type RateTableName,
  type Resource,
  type SaveBaselineOptions,
  type Task,
  type TaskId,
  type TaskScheduleEdit,
  type TaskScheduleEditResult,
  type ProgressReportResult,
  type RecordActualWorkInput,
  type SetTaskProgressInput,
  type TaskProgressMode,
  type TaskWorkflowStatus,
  type TaskCustomFieldResult,
  type WeekDays,
} from '@bigint-project/core';

import {
  summarizePasteWarnings,
  type PasteImportResult,
} from '../components/grid/paste-import';
import type { TaskImportPlanRow, XlsxProjectReplacePlan, XlsxWorkbookPreview } from '../integration/xlsx-workbook';
import type { ProjectTemplate } from '../persistence/project-template';
import * as commands from './commands';
import {
  canRedo as historyCanRedo,
  canUndo as historyCanUndo,
  commitHistory,
  emptyHistory,
  redo as historyRedo,
  undo as historyUndo,
  type HistoryState,
} from './history';
import { DEFAULT_LEVELING_OPTIONS, runLeveling, type LevelingOptions, type LevelingResult } from './leveling';
import { runSchedule, runSetBaseline } from './scheduling';
import { readStoredDependencyLineMode, writeStoredDependencyLineMode } from './view-preferences';
import {
  DEFAULT_COLUMNS,
  type ActiveCell,
  type ColumnDef,
  cloneDefaultReportPresets,
  sanitizeReportPreset,
  sanitizeReportPresets,
  type CreateReportPresetInput,
  type DependencyLineMode,
  type FileState,
  type ReportPreset,
  type ScheduleMode,
  type ScheduleState,
  type TimeScale,
  type UpdateReportPresetInput,
  type ViewName,
} from './types';

/** 排程重算的 debounce 間隔（毫秒）。 */
export const SCHEDULE_DEBOUNCE_MS = 400;

const emptyFileExtras: Pick<FileState, 'unknownTopLevelFields' | 'unknownViewFields'> = {
  unknownTopLevelFields: {},
  unknownViewFields: {},
};

const initialFileState: FileState = {
  filePath: null,
  isDirty: false,
  recentFiles: [],
  lastSavedAt: null,
  isSaving: false,
  isLoading: false,
  ...emptyFileExtras,
};

const initialScheduleState: ScheduleState = {
  mode: 'auto',
  isScheduling: false,
  diagnostics: [],
  lastError: null,
  lastRunAt: null,
};

/**
 * 資源撫平的 UI 狀態。
 *
 * `isRunning` 存在的唯一理由是「撫平約需 1～2 秒」：UI 必須在運算開始前先渲染
 * 進行中狀態。`projectBefore` 保留撫平前的專案，讓撫平前後對照可以顯示真正的
 * 原始開始日（撫平後 `project` 已被覆寫，來源就沒了）。
 */
export interface LevelingUiState {
  readonly isRunning: boolean;
  readonly result: LevelingResult | null;
  readonly projectBefore: Project | null;
  readonly error: string | null;
}

const initialLevelingState: LevelingUiState = {
  isRunning: false,
  result: null,
  projectBefore: null,
  error: null,
};

/**
 * 所有 modal 對話框的「全關」狀態，也是它們互斥的單一來源。
 *
 * 每個 `openXxx()` 都先攤平這一包再打開自己，因此新增對話框只要在這裡補一個
 * 欄位，就自動與其餘所有對話框互斥——不必記得去改每一個 `openXxx()`。原生
 * 選單在對話框開著時仍可點擊，漏掉任何一個組合就會出現兩個 modal 疊在一起、
 * 焦點互搶。`pendingXlsxImport` 不在其中：它是精靈式的匯入流程，由 `.xlsx`
 * 檔案讀取結果驅動，不由選單直接開關。
 */
const CLOSED_DIALOGS = {
  isProjectInfoOpen: false,
  isBaselineManagerOpen: false,
  isTaskDetailsOpen: false,
  isTaskProgressOpen: false,
  isCustomFieldsOpen: false,
  isTemplateExportOpen: false,
  pendingTemplate: null,
} as const;

export interface LoadProjectFileExtras {
  readonly unknownTopLevelFields?: Readonly<Record<string, unknown>>;
  readonly unknownViewFields?: Readonly<Record<string, unknown>>;
}

export type CreateTaskCustomFieldInput = Omit<CustomFieldDefinition, 'id' | 'scope' | 'formula' | 'rollup'>;

let customFieldSequence = 0;
let reportPresetSequence = 0;
function nextCustomFieldId(project: Project): CustomFieldId {
  let candidate: CustomFieldId;
  do {
    customFieldSequence += 1;
    candidate = customFieldId(`custom-${Date.now().toString(36)}-${customFieldSequence}`);
  } while (project.customFields.some((field) => field.id === candidate));
  return candidate;
}

function nextReportPresetId(presets: readonly ReportPreset[]): string {
  const used = new Set(presets.map((preset) => preset.id));
  let id: string;
  do {
    reportPresetSequence += 1;
    id = `report-${reportPresetSequence}`;
  } while (used.has(id));
  return id;
}

function hasDuplicatePresetName(presets: readonly ReportPreset[], name: string, exceptId?: string): boolean {
  const key = name.toLocaleLowerCase();
  return presets.some((preset) => preset.id !== exceptId && preset.name.toLocaleLowerCase() === key);
}

export interface ProjectState {
  // --- 核心 ---
  readonly project: Project;
  readonly history: HistoryState;
  /** Host-level policy. `true` means project mutations must fail closed. */
  readonly hostPolicy: { readonly readOnly: boolean };
  /**
   * 單調遞增計數器，`project` 每被替換一次（含 `loadProject`/`newProject`
   * 切換整個專案）就 +1，**永不重置**。存檔流程用它偵測「這次非同步寫入
   * 開始之後，目前是不是還是同一個 project 版本」，見 `markSaved()`。
   *
   * 必須永不重置：這是身分比對用的計數器，一旦在某個時機被歸零，
   * 舊專案存檔飛行中時使用者若切換到另一個檔案，新專案的 revision 有機會
   * 撞上舊存檔記下的 `savedRevision`，導致存檔完成時錯誤地清除新專案的
   * `isDirty`、甚至把新專案綁到舊的存檔路徑上。
   */
  readonly revision: number;

  // --- 選取模型 ---
  readonly selectedTaskIds: readonly string[];
  readonly selectedResourceIds: readonly string[];
  readonly activeCell: ActiveCell | null;

  // --- 視圖狀態 ---
  readonly activeView: ViewName;
  readonly timeScale: TimeScale;
  readonly collapsedTaskIds: readonly string[];
  readonly columns: readonly ColumnDef[];
  readonly showBaseline: boolean;
  readonly showCriticalPath: boolean;
  /**
   * 依賴線顯示範圍。**不寫進 `.bproj`** —— 這是看的人的偏好，見
   * `view-preferences.ts`；持久化走 localStorage，開任何檔案都沿用同一個設定。
   */
  readonly dependencyLineMode: DependencyLineMode;
  readonly scrollTop: number;
  readonly isProjectInfoOpen: boolean;
  readonly isBaselineManagerOpen: boolean;
  readonly isTaskDetailsOpen: boolean;
  readonly isTaskProgressOpen: boolean;
  readonly isCustomFieldsOpen: boolean;
  readonly pendingXlsxImport: XlsxWorkbookPreview | null;
  readonly isTemplateExportOpen: boolean;
  /** 已讀入並驗證、等待使用者確認名稱與排程錨點的專案範本。 */
  readonly pendingTemplate: ProjectTemplate | null;
  /** 使用者儲存的報表定義；屬於 .bproj view state，不屬於 core Project。 */
  readonly reportPresets: readonly ReportPreset[];
  readonly activeReportPresetId: string | null;

  // --- 檔案 / 排程 ---
  readonly file: FileState;
  readonly schedule: ScheduleState;
  readonly leveling: LevelingUiState;
  /**
   * 最近一次外部貼上的警告彙總（日期月日順序無法判定、N 筆日期／工期無法解析、
   * 前置任務對不到等）。null 代表沒有警告；由視圖層顯示成橫幅。
   */
  readonly pasteWarning: string | null;
  /**
   * 最近一次「工期／開始日／完成日」編輯被擋下的原因（例如編輯摘要任務的日期、
   * 完成日早於開始日）。null 代表沒有警告；由視圖層顯示成橫幅。
   *
   * 為什麼不沿用 `pasteWarning`：兩者的觸發來源與消失時機完全不同，共用一個欄位
   * 會讓「貼上警告還在畫面上時編輯了一格日期」互相蓋掉對方的訊息。
   */
  readonly taskEditWarning: string | null;

  // --- 生命週期 ---
  /**
   * 完全取代 project（開新檔、讀檔用）；會清空歷史與選取。
   * `revision` 不會被重置（見上方欄位註解——它是永不歸零的單調計數器，
   * 只會繼續往上加，藉此確保飛行中的舊存檔不會誤判成新專案已存檔）。
   * `fileExtras` 帶入從 `.bproj` 讀到、這版程式不認識的欄位，讓下次存檔時
   * 原樣寫回；不帶代表全新專案，未知欄位重置為空。
   */
  loadProject(project: Project, filePath: string | null, fileExtras?: LoadProjectFileExtras): void;
  newProject(): void;
  /** Synchronize the embedding host's policy without changing the loaded project. */
  setHostReadOnly(readOnly: boolean): void;

  // --- Undo / redo ---
  undo(): void;
  redo(): void;
  canUndo(): boolean;
  canRedo(): boolean;

  // --- Commands（唯一允許改動 project 的入口） ---
  updateTask(id: TaskId, patch: Partial<Omit<Task, 'id' | 'customFieldValues'>>, options?: { mergeKey?: string }): void;
  /** 逐日實際工時回報；依任務的 progressMode 決定是否反推進度。 */
  recordActualWork(input: RecordActualWorkInput): ProgressReportResult;
  /** 直接指定任務進度（可同時切換 progressMode）。 */
  setTaskProgress(input: SetTaskProgressInput): ProgressReportResult;
  /** 只切換進度模式；不動進度、工時與日期。 */
  setTaskProgressMode(taskId: TaskId, mode: TaskProgressMode): ProgressReportResult;
  /** 設定看板狀態；`done` 會把進度推到 100%。 */
  setTaskWorkflowStatus(taskId: TaskId, status: TaskWorkflowStatus): ProgressReportResult;
  createTaskCustomField(input: CreateTaskCustomFieldInput): TaskCustomFieldResult;
  renameTaskCustomField(id: CustomFieldId, name: string): TaskCustomFieldResult;
  deleteTaskCustomField(id: CustomFieldId): TaskCustomFieldResult;
  setTaskCustomFieldValue(taskId: TaskId, fieldId: CustomFieldId, value: CustomFieldValue): TaskCustomFieldResult;
  insertTask(params: commands.InsertTaskParams): TaskId;
  deleteTasks(taskIds: readonly TaskId[]): void;
  indentTasks(taskIds: readonly TaskId[]): void;
  outdentTasks(taskIds: readonly TaskId[]): void;
  /**
   * 把選取的任務在清單中上移／下移一格。連同其子樹一起移動。
   *
   * 一次呼叫 = **一個** undo 步驟（走 `commit()`，因此 WBS／階層／dirty／
   * 排程觸發全部沿用既有收斂點）。搬移後 `outlineLevel` 與 `parentId`
   * **維持不變**——搬移是「同層兄弟互換位置」，升降階請用縮排／凸排。
   * 完整規則見 `commands.moveTasks()` 的 JSDoc。
   *
   * 到達父任務底下的頂／底時靜默不動（不越界、不產生歷史）。
   */
  moveTasks(taskIds: readonly TaskId[], direction: commands.MoveDirection): void;

  // --- 工期 ↔ 日期互推 ---
  /**
   * 改工期 → 依開始日與工作日曆推算新的完成日（工時依 taskType 連動）。
   * 摘要任務會被擋下並回傳 `ok: false`，同時寫入 `taskEditWarning` 供橫幅顯示。
   */
  setTaskDuration(id: TaskId, durationMinutes: number): TaskScheduleEditResult;
  /** 改開始日 → **保持工期不變**，推算新的完成日。 */
  setTaskStart(id: TaskId, start: ISODateTime): TaskScheduleEditResult;
  /** 改完成日 → 依開始日推算新的工期（工時依 taskType 連動）。 */
  setTaskFinish(id: TaskId, finish: ISODateTime): TaskScheduleEditResult;
  /** 關閉日期／工期編輯被擋下的警告橫幅。 */
  dismissTaskEditWarning(): void;
  addDependency(params: {
    predecessorId: TaskId;
    successorId: TaskId;
    type?: DependencyType;
    lagMinutes?: number;
  }): void;
  removeDependency(id: Dependency['id']): void;
  /**
   * 以 `next` 整批取代「以 `taskId` 為後續任務」的所有相依關係。
   *
   * 表格的「前置任務」欄位一次編輯就是一次整批取代（見 `TaskGridProps.onDependencyChange`）。
   * 如果拆成多次 add/remove，一次欄位編輯會產生好幾筆歷史紀錄，使用者按一次
   * 復原只會退回一半——所以整批取代必須是**單一**歷史步驟。
   */
  replaceTaskDependencies(taskId: TaskId, next: readonly Dependency[]): void;
  /**
   * 更新單一資源的欄位（資源工作表的儲存格編輯）。
   * `updateProjectMeta` 只涵蓋專案層級的中繼欄位，不含 resources 陣列。
   */
  updateResource(id: Resource['id'], patch: Partial<Omit<Resource, 'id' | 'uid' | 'isBudget'>>): void;
  /** 新增一個 Work 資源，回傳其穩定的 ResourceId。 */
  addResource(overrides?: commands.AddResourceOverrides): Resource['id'];
  /** 刪除資源及其所有指派；未知／空 id 清單不產生歷史。 */
  deleteResources(resourceIds: readonly Resource['id'][]): void;
  /** 切換預算資源狀態；已有一般指派時，command 會拒絕切換。 */
  setResourceBudgetStatus(resourceId: Resource['id'], isBudget: boolean): void;
  /** 原子替換專案摘要預算 allocation，並由 core 驗證。 */
  setProjectBudgetAssignments(assignments: readonly BudgetAssignment[]): void;
  assignResource(params: commands.AssignResourceParams): void;
  /** Select cost rate table A-E for one existing assignment. */
  setAssignmentRateTable(assignmentId: Assignment['id'], rateTable: RateTableName): void;
  unassignResource(assignmentId: Assignment['id']): void;
  /**
   * 看板「依資源」欄之間搬卡：在同一步歷史裡移除來源指派並建立目標指派，
   * 一次拖拉只對應一次 undo。`fromAssignmentId` 為 null 代表卡片來自「未指派」欄，
   * `toResourceId` 為 null 代表拖進「未指派」欄；目標資源已經指派到該任務時
   * 只移除來源，不用來源的 units／work 覆寫目標既有的值。
   */
  reassignResource(
    taskId: TaskId,
    fromAssignmentId: Assignment['id'] | null,
    toResourceId: Assignment['resourceId'] | null,
    units: number,
    work: number,
  ): void;
  setBaseline(options: SaveBaselineOptions): void;
  clearBaseline(number: BaselineNumber): void;
  updateProjectMeta(patch: commands.ProjectMetaPatch): void;
  /**
   * 從 Excel / MS Project 匯出表整批貼上（覆蓋既有列 + 新增超出的列 + 建立依賴）。
   *
   * 走 `commit()`：整批只產生**一筆**歷史（貼 114 列 undo 一次就全部退回）、
   * 只 bump 一次 `revision`、只觸發一次 debounce 排程重算。
   */
  pasteTasks(result: PasteImportResult): void;
  /** Apply a validated XLSX task plan as one undoable change. */
  importXlsxTasks(rows: readonly TaskImportPlanRow[], mode: commands.XlsxTaskImportMode): void;
  /** Atomically replace imported tasks/resources/dependencies/assignments from a strict XLSX plan. */
  replaceProjectFromXlsx(plan: XlsxProjectReplacePlan, options?: commands.XlsxProjectImportOptions): void;
  /** 關閉貼上警告橫幅。 */
  dismissPasteWarning(): void;
  /**
   * 逐任務切換「手動排程 / 自動排程」（`Task.isManuallyScheduled`）。
   *
   * 與專案層級的 `setScheduleMode()`（那個管的是「排程何時重算」）**語義完全不同**：
   * 這裡管的是「引擎可不可以移動這顆任務」。手動排程的任務日期由使用者決定，
   * 引擎不會搬它，但它仍然參與依賴計算、照樣把後續任務往後推
   * （見 `packages/core/src/engine/scheduling.ts`）。
   *
   * 走 `commit()`：整批只產生**一筆**歷史（多選切換 undo 一次全部退回）、
   * 標 dirty、重算 WBS、觸發排程重排。
   * 全部任務都已經是目標狀態時不做任何事（不產生歷史、不標 dirty）。
   */
  setTasksScheduleMode(taskIds: readonly TaskId[], manual: boolean): void;

  // --- 工作日曆 ---
  /**
   * 新增一份工作日曆（`baseCalendarId` 為 null 代表它自己就是基礎日曆）。
   *
   * 五個日曆 action 全部走 `commit()`，理由不是「順手共用」而是必須：日曆決定
   * 哪些時段是工作時間，任何一項改動都會改變任務日期的推算結果，所以要跟其他
   * 編輯一樣重算 WBS、標 dirty、bump revision、觸發排程重排（單一 undo 步驟）。
   */
  addCalendar(name: string, baseCalendarId: CalendarId | null): void;
  /**
   * 刪除一份工作日曆，並把指向它的參照一併收乾淨（任務／資源改用專案預設、
   * 子日曆改繼承被刪日曆的 base、被刪的若是專案預設則自動改指剩餘的第一份）。
   * 只剩一份日曆時不刪（專案必須至少有一份日曆），完整規則見
   * `commands.deleteCalendar()`。
   */
  deleteCalendar(id: CalendarId): void;
  setDefaultCalendar(id: CalendarId): void;
  updateCalendarWeekDays(id: CalendarId, weekDays: WeekDays): void;
  updateCalendarExceptions(id: CalendarId, exceptions: readonly CalendarException[]): void;

  // --- 報表 preset（持久化 view state；不進 Project undo） ---
  createReportPreset(input: CreateReportPresetInput): string | null;
  updateReportPreset(id: string, patch: UpdateReportPresetInput): void;
  duplicateReportPreset(id: string): string | null;
  deleteReportPreset(id: string): void;
  selectReportPreset(id: string | null): void;
  /** 檔案載入專用：收斂資料但不標 dirty、不 bump revision。 */
  restoreReportPresetState(presets: unknown, activeId: unknown): void;

  // --- 排程觸發 ---
  setScheduleMode(mode: ScheduleMode): void;
  /** 手動觸發一次排程重算：視同一般 command，進歷史、標 dirty。 */
  runScheduleNow(): void;

  // --- 資源撫平 ---
  /**
   * 執行資源撫平。回傳 Promise，但**進行中狀態是同步先寫入的**：呼叫後
   * `leveling.isRunning` 立刻為 true，實際運算延到下一個 macrotask，讓 UI
   * 有機會先畫出進行中回饋（撫平約 1～2 秒，不能讓畫面凍結無回饋）。
   * 撫平結果視同一般 command：進歷史、可 undo、標 dirty。
   */
  levelResources(options?: LevelingOptions): Promise<void>;
  /** 關閉撫平前後對照面板（不影響已套用的撫平結果）。 */
  dismissLevelingResult(): void;

  // --- 選取 ---
  selectTasks(taskIds: readonly string[]): void;
  selectResources(resourceIds: readonly string[]): void;
  setActiveCell(cell: ActiveCell | null): void;

  // --- 視圖 ---
  setActiveView(view: ViewName): void;
  setTimeScale(scale: TimeScale): void;
  setCollapsedTaskIds(taskIds: readonly string[]): void;
  toggleCollapsed(taskId: string): void;
  setColumns(columns: readonly ColumnDef[]): void;
  setShowBaseline(show: boolean): void;
  setShowCriticalPath(show: boolean): void;
  setDependencyLineMode(mode: DependencyLineMode): void;
  setScrollTop(scrollTop: number): void;
  openProjectInfo(): void;
  closeProjectInfo(): void;
  openBaselineManager(): void;
  closeBaselineManager(): void;
  openTaskDetails(): void;
  closeTaskDetails(): void;
  openTaskProgress(): void;
  closeTaskProgress(): void;
  openCustomFields(): void;
  closeCustomFields(): void;
  openXlsxImport(workbook: XlsxWorkbookPreview): void;
  closeXlsxImport(): void;
  openTemplateExport(): void;
  closeTemplateExport(): void;
  openTemplateCreate(template: ProjectTemplate): void;
  closeTemplateCreate(): void;

  // --- 檔案狀態（實際 I/O 由 persistence/ 層呼叫這些 setter） ---
  markSaving(isSaving: boolean): void;
  /**
   * 標記一次成功寫入。`savedRevision` 應為「開始寫入那一刻」的 `revision`
   * （見 `persistence/file-service.ts` 的 `writeProjectTo`）。只有在目前
   * `revision` 仍等於 `savedRevision` 時才會更新 `file` 狀態
   * （`filePath`/`isDirty`/`lastSavedAt`/`recentFiles` 一起更新或一起不動，
   * 不會只更新其中一部分）。`revision` 不吻合代表：
   * (a) 同一份文件在寫入期間又被編輯過，或
   * (b) 使用者在寫入期間切去了另一個專案（`loadProject`/`newProject` 也會
   *     推進 `revision`）。
   * 不論哪一種，這次（過期的）寫入結果都不該覆寫目前的 `file` 狀態——
   * 情境 (a) 會把新編輯誤判成已存檔，情境 (b) 會把目前這份文件錯誤地
   * 綁到不屬於它的舊存檔路徑上。只有 `isSaving` 一律收尾。
   * 不傳 `savedRevision`（例如剛讀檔完成時單純想清 dirty）則無條件更新。
   */
  markSaved(filePath: string, savedRevision?: number): void;
  markLoading(isLoading: boolean): void;
  setDirty(isDirty: boolean): void;
}

let scheduleTimer: ReturnType<typeof setTimeout> | null = null;

/** Raised only by result-returning editor commands that cannot express a no-op result. */
export class HostReadOnlyError extends Error {
  constructor(action: string) {
    super(`Host read-only policy blocked ${action}.`);
    this.name = 'HostReadOnlyError';
  }
}

function clearScheduleTimer(): void {
  if (scheduleTimer !== null) {
    clearTimeout(scheduleTimer);
    scheduleTimer = null;
  }
}

export const useProjectStore = create<ProjectState>((set, get) => {
  function assertHostAllowsMutation(action: string): void {
    if (get().hostPolicy.readOnly) throw new HostReadOnlyError(action);
  }

  /**
   * WBS 重算的單一收斂點：把 `mutate()`／引擎回傳的候選 project 定案成
   * 「這次改動最終版本」——WBS 在這裡重算一次，`after` 同時是 history
   * 快照的 `after` 與最終寫進 state 的值，兩者保證是同一個物件，不會有
   * 「畫面顯示重算後、undo 卻回到重算前」的不一致。
   *
   * 為什麼這是唯一收斂點、不是「記得在每個 command 後面加一行」：
   * `commit()`（覆蓋 updateTask／deleteTasks／indentTasks／outdentTasks／
   * addDependency／replaceTaskDependencies／removeDependency／
   * updateResource／addResource／deleteResources／assignResource／unassignResource／updateProjectMeta——
   * 這是目前唯一一個「同時處理 history／dirty／revision／自動排程觸發」
   * 的共用實作，任何新 command 只要想要這些既有行為就必須透過它，沒有
   * 平行的第二套實作）都呼叫這裡；`insertTask`、`setBaseline`、`clearBaseline` 因為回傳值
   * 或排程觸發需求不同（新增要回傳 id、基準線操作不應重跑排程）沒有直接共用
   * `commit()`，但一樣呼叫這個函式取得同一份保證。`applyScheduleResult`／
   * `levelResources` 因為 history 處理方式各自不同（前者可能併入既有那
   * 一步、後者要一併更新撫平面板狀態），沒有呼叫這個函式，但各自對著
   * 同一個 `after` 值呼叫 `withWbsCodes()` 一次、同時用於 history 快照
   * 與最終 state（見各自函式內的說明）。
   *
   * `undo`/`redo` 刻意不呼叫這裡：它們只是把游標移到 history 裡「已經在
   * 寫入當下算過 WBS」的既有快照，不產生新版本，重算只會讓 Project 頂層
   * 物件白白多換一顆新的（`withWbsCodes()` 每次呼叫都回傳新的頂層物件，
   * 即使內容沒變），沒有正確性上的好處。
   *
   * `withWbsCodes()` 冪等（未變動的 Task 沿用原參照），這裡無條件套用，
   * 不需要先判斷這次改動是不是「結構性」的。
   */
  function finalizeProjectChange(
    before: Project,
    historyBefore: HistoryState,
    rawAfter: Project,
    commitOptions: { readonly label: string; readonly mergeKey?: string },
  ): { readonly project: Project; readonly history: HistoryState; readonly changed: boolean } {
    if (rawAfter === before) {
      return { project: before, history: historyBefore, changed: false };
    }
    const after = withWbsCodes(rawAfter);
    const { history } = commitHistory(historyBefore, before, after, commitOptions);
    return { project: after, history, changed: true };
  }

  /**
   * 執行一次排程重算並套用結果。
   *
   * - `mode: 'manual'`（使用者主動觸發）：視同一般 command，走
   *   `commitHistory` 產生新的一步、標記 dirty——可復原。
   * - `mode: 'auto'`（debounce 後自動觸發）：不額外開一筆歷史（避免每次
   *   打字都多一步 undo），而是把排程結果「併入」觸發它的那一筆歷史紀錄
   *   的 `after`；但不論哪種模式，只要 project 實際變動就一定標記 dirty
   *   ——排程造成的變更絕不能因為「合併進歷史」而被誤判成不用存檔。
   *   若當下沒有可併入的歷史（理論上不會發生，防禦性處理），退化為
   *   建立新的一筆歷史。
   */
  function applyScheduleResult(mode: 'auto' | 'manual'): void {
    if (get().hostPolicy.readOnly) {
      set((state) => ({ schedule: { ...state.schedule, isScheduling: false } }));
      return;
    }
    const before = get();
    const result = runSchedule(before.project);

    set((state) => ({
      schedule: {
        ...state.schedule,
        isScheduling: false,
        diagnostics: result.diagnostics,
        lastError: result.error,
        lastRunAt: new Date().toISOString(),
      },
    }));

    if (result.error !== null || result.project === before.project) return;

    // 只在這裡呼叫一次 withWbsCodes()，往下兩條分支（併入既有歷史 / 開新
    // 一筆歷史）都用同一個 `after`，history 快照與最終 state 保證一致。
    const after = withWbsCodes(result.project);

    if (mode === 'auto' && before.history.past.length > 0) {
      const top = before.history.past[before.history.past.length - 1];
      if (top === undefined) return; // 不可能發生（上面已檢查 length > 0），純粹讓 TS 安心
      const past = before.history.past.slice(0, -1).concat({ ...top, after });
      set((state) => ({
        project: after,
        revision: state.revision + 1,
        history: { past, future: before.history.future },
        file: { ...state.file, isDirty: true },
      }));
      return;
    }

    const { history } = commitHistory(before.history, before.project, after, {
      label: mode === 'manual' ? 'schedule (manual)' : 'schedule (auto)',
    });
    set((state) => ({
      project: after,
      revision: state.revision + 1,
      history,
      file: { ...state.file, isDirty: true },
    }));
  }

  function scheduleAutoRun(): void {
    if (get().hostPolicy.readOnly || get().schedule.mode !== 'auto') return;
    clearScheduleTimer();
    scheduleTimer = setTimeout(() => {
      scheduleTimer = null;
      applyScheduleResult('auto');
    }, SCHEDULE_DEBOUNCE_MS);
  }

  function persistReportPresetState(presets: readonly ReportPreset[], activeReportPresetId: string | null): void {
    if (get().hostPolicy.readOnly) return;
    set((state) => ({
      reportPresets: presets,
      activeReportPresetId,
      revision: state.revision + 1,
      file: { ...state.file, isDirty: true },
    }));
  }

  /** 共用的 commit 邏輯：套用 mutate、（透過 finalizeProjectChange）重算 WBS、寫入歷史、標記 dirty、bump revision、觸發排程。 */
  function commit(
    label: string,
    mutate: (project: Project) => Project,
    mergeKey?: string,
  ): void {
    if (get().hostPolicy.readOnly) return;
    const state = get();
    const result = finalizeProjectChange(state.project, state.history, mutate(state.project), { label, mergeKey });
    if (!result.changed) return;
    set((s) => ({
      project: result.project,
      revision: s.revision + 1,
      history: result.history,
      file: { ...s.file, isDirty: true },
    }));
    scheduleAutoRun();
  }

  /**
   * 「工期 ↔ 開始日 ↔ 完成日」互推的共用入口。
   *
   * 日期運算全部在 `computeTaskScheduleEdit()`（core 的純函式，一律走
   * calendar-engine）裡完成，這裡只負責三件 store 的事：把被擋下的原因寫進
   * `taskEditWarning`、把成功的 patch 透過 `commit()` 套上去（因此自動享有
   * WBS 收斂／歷史／dirty／排程重算）、並把結果回傳給呼叫端（UI 需要立刻
   * 知道這格編輯有沒有被接受，才能決定要不要把儲存格還原）。
   *
   * `mergeKey` 以「任務 + 編輯的欄位」為單位：同一格連續微調併成一步 undo，
   * 但「改工期」與「改完成日」不會互相吞併——它們是兩個不同的意圖。
   */
  function applyTaskScheduleEdit(id: TaskId, edit: TaskScheduleEdit): TaskScheduleEditResult {
    assertHostAllowsMutation(`setTask:${edit.kind}`);
    const result = computeTaskScheduleEdit(get().project, id, edit);
    if (!result.ok) {
      set({ taskEditWarning: result.message });
      return result;
    }
    set({ taskEditWarning: null });
    commit(
      `setTask:${edit.kind}`,
      (project) => commands.updateTask(project, id, result.patch),
      `setTask:${edit.kind}:${id}`,
    );
    return result;
  }

  return {
    project: fixtures.makeProject({ name: 'Untitled Project' }),
    history: emptyHistory,
    hostPolicy: { readOnly: false },
    revision: 0,

    selectedTaskIds: [],
    selectedResourceIds: [],
    activeCell: null,

    activeView: 'gantt',
    timeScale: 'week',
    collapsedTaskIds: [],
    columns: DEFAULT_COLUMNS,
    showBaseline: false,
    showCriticalPath: false,
    dependencyLineMode: readStoredDependencyLineMode(),
    scrollTop: 0,
    ...CLOSED_DIALOGS,
    pendingXlsxImport: null,
    reportPresets: cloneDefaultReportPresets(),
    activeReportPresetId: cloneDefaultReportPresets()[0]?.id ?? null,

    file: initialFileState,
    schedule: initialScheduleState,
    leveling: initialLevelingState,
    pasteWarning: null,
    taskEditWarning: null,

    loadProject: (project, filePath, fileExtras) => {
      clearScheduleTimer();
      // 開新檔／讀檔一律重算 WBS：讀進來的 .bproj 可能是舊版程式存的、
      // 或被外部工具改過，`task.wbs` 不保證與目前的 outlineLevel／順序一致。
      // 摘要進度同理：它是由子任務推導的儲存值，檔案裡可能是排程當下、
      // 子任務還沒填進度時的舊值，載入時一併收斂才不會出現「子任務全 100%、
      // 摘要 0%」。兩者都只是重算衍生值，不改使用者資料，因此不標 dirty。
      const normalized = rollUpSummaryProgress(withWbsCodes(project));
      set((s) => ({
        project: normalized,
        history: emptyHistory,
        // revision 刻意不重置——見欄位註解：這是身分比對用的單調計數器，
        // 重置會讓「切換專案」這個最容易丟資料的時機發生 revision 碰撞。
        revision: s.revision + 1,
        selectedTaskIds: [],
        selectedResourceIds: [],
        activeCell: null,
        ...CLOSED_DIALOGS,
        pendingXlsxImport: null,
        reportPresets: cloneDefaultReportPresets(),
        activeReportPresetId: cloneDefaultReportPresets()[0]?.id ?? null,
        file: {
          ...initialFileState,
          filePath,
          recentFiles: s.file.recentFiles,
          unknownTopLevelFields: fileExtras?.unknownTopLevelFields ?? {},
          unknownViewFields: fileExtras?.unknownViewFields ?? {},
        },
        schedule: initialScheduleState,
        leveling: initialLevelingState,
        pasteWarning: null,
        taskEditWarning: null,
      }));
    },

    newProject: () => {
      if (get().hostPolicy.readOnly) return;
      get().loadProject(fixtures.makeProject({ name: 'Untitled Project' }), null);
    },

    setHostReadOnly: (readOnly) => {
      if (get().hostPolicy.readOnly === readOnly) return;
      if (readOnly) clearScheduleTimer();
      set((state) => ({
        hostPolicy: { readOnly },
        ...(readOnly
          ? {
              ...CLOSED_DIALOGS,
              pendingXlsxImport: null,
              leveling: state.leveling.isRunning ? initialLevelingState : state.leveling,
              schedule: { ...state.schedule, isScheduling: false },
            }
          : {}),
      }));
    },

    undo: () => {
      if (get().hostPolicy.readOnly) return;
      const state = get();
      const result = historyUndo(state.history, state.project);
      if (result === null) return;
      clearScheduleTimer();
      // 不在這裡再呼叫 withWbsCodes()：history 裡的每一筆快照，在被寫入
      // history 的當下就已經透過 finalizeProjectChange／applyScheduleResult／
      // levelResources 重算過 WBS（那三個是整個 store 唯一會建立新 history
      // 條目的地方）。undo/redo 只是把游標移到既有快照，不產生新版本，
      // 再重算一次除了讓 Project 頂層物件白白換一顆新的（withWbsCodes 每次
      // 呼叫都會回傳新的頂層物件，即使內容沒變），沒有任何額外好處。
      set((s) => ({
        project: result.project,
        revision: s.revision + 1,
        history: result.history,
        file: { ...s.file, isDirty: true },
      }));
    },

    redo: () => {
      if (get().hostPolicy.readOnly) return;
      const state = get();
      const result = historyRedo(state.history, state.project);
      if (result === null) return;
      clearScheduleTimer();
      set((s) => ({
        project: result.project,
        revision: s.revision + 1,
        history: result.history,
        file: { ...s.file, isDirty: true },
      }));
    },

    canUndo: () => historyCanUndo(get().history),
    canRedo: () => historyCanRedo(get().history),

    updateTask: (id, patch, options) => {
      commit(
        'updateTask',
        (project) => commands.updateTask(project, id, patch),
        options?.mergeKey ?? `updateTask:${id}`,
      );
    },

    recordActualWork: (input) => {
      assertHostAllowsMutation('recordActualWork');
      const result = recordProjectActualWork(get().project, input);
      if (!result.ok) return result;
      commit('recordActualWork', () => result.project);
      return result;
    },

    setTaskProgress: (input) => {
      assertHostAllowsMutation('setTaskProgress');
      const result = setProjectTaskProgress(get().project, input);
      // 與 applyTaskScheduleEdit 同一套：被擋下的原因寫進 taskEditWarning 供橫幅顯示，
      // 表格／甘特圖直接編輯完成百分比時才有地方看到「為什麼沒生效」。
      if (!result.ok) {
        set({ taskEditWarning: result.error.message });
        return result;
      }
      set({ taskEditWarning: null });
      commit('setTaskProgress', () => result.project);
      return result;
    },

    setTaskProgressMode: (taskId, mode) => {
      assertHostAllowsMutation('setTaskProgressMode');
      const result = setProjectTaskProgressMode(get().project, taskId, mode);
      if (!result.ok) return result;
      commit('setTaskProgressMode', () => result.project);
      return result;
    },

    setTaskWorkflowStatus: (taskId, status) => {
      assertHostAllowsMutation('setTaskWorkflowStatus');
      const result = setProjectTaskWorkflowStatus(get().project, taskId, status);
      if (!result.ok) return result;
      commit('setTaskWorkflowStatus', () => result.project);
      return result;
    },

    createTaskCustomField: (input) => {
      assertHostAllowsMutation('createTaskCustomField');
      const definition: CustomFieldDefinition = {
        ...input,
        id: nextCustomFieldId(get().project),
        scope: 'Task',
        formula: null,
        rollup: 'None',
      };
      const result = createProjectTaskCustomField(get().project, definition);
      if (!result.ok) return result;
      commit('createTaskCustomField', () => result.project);
      set((state) => state.columns.some((column) => column.field === definition.id)
        ? state
        : { columns: [...state.columns, { id: `custom:${definition.id as string}`, field: definition.id as string, label: definition.name, width: 120, visible: true }] });
      return result;
    },

    renameTaskCustomField: (id, name) => {
      assertHostAllowsMutation('renameTaskCustomField');
      const result = renameProjectTaskCustomField(get().project, id, name);
      if (!result.ok) return result;
      commit('renameTaskCustomField', () => result.project);
      return result;
    },

    deleteTaskCustomField: (id) => {
      assertHostAllowsMutation('deleteTaskCustomField');
      const result = deleteProjectTaskCustomField(get().project, id);
      if (!result.ok) return result;
      // 保留 view column：definition 不在時 adapter 會隱藏；undo 恢復 definition
      // 後欄位會自動回來，讓刪除與復原保持一致。
      commit('deleteTaskCustomField', () => result.project);
      return result;
    },

    setTaskCustomFieldValue: (taskIdToUpdate, fieldId, value) => {
      assertHostAllowsMutation('setTaskCustomFieldValue');
      const result = setProjectTaskCustomFieldValue(get().project, taskIdToUpdate, fieldId, value);
      if (!result.ok) return result;
      commit('setTaskCustomFieldValue', () => result.project, `customField:${taskIdToUpdate}:${fieldId}`);
      return result;
    },

    insertTask: (params) => {
      assertHostAllowsMutation('insertTask');
      const state = get();
      const raw = commands.insertTask(state.project, params);
      const result = finalizeProjectChange(state.project, state.history, raw.project, { label: 'insertTask' });
      set((s) => ({
        project: result.project,
        revision: s.revision + 1,
        history: result.history,
        file: { ...s.file, isDirty: true },
      }));
      scheduleAutoRun();
      // withWbsCodes() 只換 wbs 欄位、不換 id，raw.taskId 在重算後的
      // result.project 裡仍然指向同一顆任務。
      return raw.taskId;
    },

    deleteTasks: (taskIds) => {
      commit('deleteTasks', (project) => commands.deleteTasks(project, taskIds));
      const remaining = get().selectedTaskIds.filter((id) => get().project.tasks.some((task) => task.id === id));
      set({
        selectedTaskIds: remaining,
        isTaskDetailsOpen: remaining.length === 1 ? get().isTaskDetailsOpen : false,
        isTaskProgressOpen: remaining.length === 1 ? get().isTaskProgressOpen : false,
      });
    },

    indentTasks: (taskIds) => {
      commit('indentTasks', (project) => commands.indentTasks(project, taskIds));
    },

    outdentTasks: (taskIds) => {
      commit('outdentTasks', (project) => commands.outdentTasks(project, taskIds));
    },

    moveTasks: (taskIds, direction) => {
      // 單一 commit → 單一 undo 步驟：順序搬移、`withHierarchy()` 收斂
      // parentId／isSummary、`finalizeProjectChange()` 收斂 WBS、
      // `scheduleAutoRun()` 觸發日期重排，全部在同一步裡完成。
      // 刻意不帶 mergeKey：連按兩次「下移」是兩個語義獨立的操作，
      // 使用者按一次復原時預期只退回一格。
      commit('moveTasks', (project) =>
        commands.withHierarchy(commands.moveTasks(project, taskIds, direction)),
      );
    },

    setTaskDuration: (id, durationMinutes) =>
      applyTaskScheduleEdit(id, { kind: 'duration', durationMinutes: minutes(durationMinutes) }),

    setTaskStart: (id, start) => applyTaskScheduleEdit(id, { kind: 'start', start }),

    setTaskFinish: (id, finish) => applyTaskScheduleEdit(id, { kind: 'finish', finish }),

    dismissTaskEditWarning: () => set({ taskEditWarning: null }),

    addDependency: (params) => {
      commit('addDependency', (project) => commands.addDependency(project, params));
    },

    replaceTaskDependencies: (targetTaskId, next) => {
      commit(`replaceTaskDependencies:${targetTaskId}`, (project) => {
        const current = project.dependencies.filter((d) => d.successorId === targetTaskId);
        // 內容完全相同就什麼都不做——否則每次離開儲存格都會重建一批新 id 的
        // dependency，讓 project 換一顆新物件，白白標 dirty 並觸發整輪重排。
        const same =
          current.length === next.length &&
          next.every((candidate) =>
            current.some(
              (existing) =>
                existing.predecessorId === candidate.predecessorId &&
                existing.type === candidate.type &&
                (existing.lagMinutes as number) === (candidate.lagMinutes as number),
            ),
          );
        if (same) return project;

        let result: Project = {
          ...project,
          dependencies: project.dependencies.filter((d) => d.successorId !== targetTaskId),
        };
        for (const dependency of next) {
          result = commands.addDependency(result, {
            predecessorId: dependency.predecessorId,
            successorId: targetTaskId,
            type: dependency.type,
            lagMinutes: dependency.lagMinutes as number,
          });
        }
        return result;
      });
    },

    removeDependency: (id) => {
      commit('removeDependency', (project) => commands.removeDependency(project, id));
    },

    updateResource: (targetResourceId, patch) => {
      commit(
        'updateResource',
        (project) => {
          const current = project.resources.find((resource) => resource.id === targetResourceId);
          // 預算 allocation 的值型別由 Resource.type 決定：Cost 只能有
          // budgetCost，Work/Material 只能有 budgetWork。已有 allocation 時拒絕
          // Cost ↔ 非 Cost 的切換，避免暫態或持久化出 core 無法驗證的 project；
          // Work ↔ Material 同屬 budgetWork，因此仍是合法的普通欄位更新。
          if (
            current !== undefined &&
            patch.type !== undefined &&
            (current.type === 'Cost') !== (patch.type === 'Cost') &&
            (project.budgetAssignments ?? []).some((assignment) => assignment.resourceId === targetResourceId)
          ) {
            return project;
          }
          let changed = false;
          const resources = project.resources.map((resource) => {
            if (resource.id !== targetResourceId) return resource;
            changed = true;
            return { ...resource, ...patch };
          });
          return changed ? { ...project, resources } : project;
        },
        `updateResource:${targetResourceId}`,
      );
    },

    addResource: (overrides) => {
      assertHostAllowsMutation('addResource');
      const state = get();
      const raw = commands.addResource(state.project, overrides);
      const result = finalizeProjectChange(state.project, state.history, raw.project, { label: 'addResource' });
      set((s) => ({
        project: result.project,
        revision: s.revision + 1,
        history: result.history,
        file: { ...s.file, isDirty: true },
      }));
      scheduleAutoRun();
      return raw.resourceId;
    },

    deleteResources: (resourceIds) => {
      commit('deleteResources', (project) => commands.deleteResources(project, resourceIds));
    },

    setResourceBudgetStatus: (resourceId, isBudget) => {
      commit('setResourceBudgetStatus', (project) =>
        commands.setResourceBudgetStatus(project, resourceId, isBudget),
      );
    },

    setProjectBudgetAssignments: (assignments) => {
      commit('setProjectBudgetAssignments', (project) =>
        commands.setProjectBudgetAssignments(project, assignments),
      );
    },

    assignResource: (params) => {
      commit('assignResource', (project) => commands.assignResource(project, params));
    },

    setAssignmentRateTable: (assignmentId, rateTable) => {
      commit(
        'setAssignmentRateTable',
        (project) => commands.setAssignmentRateTable(project, assignmentId, rateTable),
        `setAssignmentRateTable:${assignmentId}`,
      );
    },

    unassignResource: (assignmentIdToRemove) => {
      commit('unassignResource', (project) => commands.unassignResource(project, assignmentIdToRemove));
    },

    reassignResource: (taskId, fromAssignmentId, toResourceId, units, work) => {
      commit('reassignResource', (project) => {
        const withoutSource =
          fromAssignmentId === null ? project : commands.unassignResource(project, fromAssignmentId);
        if (toResourceId === null) return withoutSource;
        const targetAlreadyAssigned = withoutSource.assignments.some(
          (assignment) => assignment.taskId === taskId && assignment.resourceId === toResourceId,
        );
        if (targetAlreadyAssigned) return withoutSource;
        return commands.assignResource(withoutSource, { taskId, resourceId: toResourceId, units, work });
      });
    },

    setBaseline: (options) => {
      if (get().hostPolicy.readOnly) return;
      const state = get();
      const result = runSetBaseline(state.project, options);
      set((s) => ({ schedule: { ...s.schedule, diagnostics: result.diagnostics, lastError: result.error } }));
      if (result.error !== null) return;
      const finalized = finalizeProjectChange(state.project, state.history, result.project, {
        label: 'setBaseline',
      });
      if (!finalized.changed) return;
      set((s) => ({
        project: finalized.project,
        revision: s.revision + 1,
        history: finalized.history,
        file: { ...s.file, isDirty: true },
      }));
    },

    clearBaseline: (number) => {
      if (get().hostPolicy.readOnly) return;
      const state = get();
      const finalized = finalizeProjectChange(
        state.project,
        state.history,
        clearProjectBaseline(state.project, number),
        { label: 'clearBaseline' },
      );
      if (!finalized.changed) return;
      set((s) => ({
        project: finalized.project,
        revision: s.revision + 1,
        history: finalized.history,
        file: { ...s.file, isDirty: true },
      }));
    },

    updateProjectMeta: (patch) => {
      commit('updateProjectMeta', (project) => commands.updateProjectMeta(project, patch));
    },

    pasteTasks: (result) => {
      if (get().hostPolicy.readOnly) return;
      // 整批一次 mutate → 一次 commit：一筆歷史、一次 revision、一次排程重算。
      commit('pasteTasks', (project) => commands.applyPastedTasks(project, result));
      set({ pasteWarning: summarizePasteWarnings(result.tasks.length, result.warnings) });
    },

    importXlsxTasks: (rows, mode) => {
      if (get().hostPolicy.readOnly) return;
      commit('importXlsxTasks', (project) => commands.applyXlsxTaskImport(project, rows, mode));
      if (mode === 'replace' && rows.length > 0) {
        set({ selectedTaskIds: [], activeCell: null, collapsedTaskIds: [], showBaseline: false });
      }
    },

    replaceProjectFromXlsx: (plan, options) => {
      if (get().hostPolicy.readOnly) return;
      const before = get().project;
      // `applyXlsxProjectImport` either returns one complete candidate or throws
      // before `commit` writes state. Therefore selection is only cleared after
      // the replacement succeeded and actually changed the project.
      commit('replaceProjectFromXlsx', (project) => commands.applyXlsxProjectImport(project, plan, options));
      if (get().project !== before) {
        set({ selectedTaskIds: [], activeCell: null, collapsedTaskIds: [], showBaseline: false });
      }
    },

    dismissPasteWarning: () => set({ pasteWarning: null }),

    setTasksScheduleMode: (taskIds, manual) => {
      if (taskIds.length === 0) return;
      const targets = new Set<TaskId>(taskIds);
      commit('setTasksScheduleMode', (project) => {
        let changed = false;
        const tasks = project.tasks.map((task) => {
          if (!targets.has(task.id) || task.isManuallyScheduled === manual) return task;
          changed = true;
          return { ...task, isManuallyScheduled: manual };
        });
        // 回傳同一顆 project 物件 → finalizeProjectChange 判定無變動，
        // 不產生歷史、不標 dirty（重複點同一個選單項目不該多一步 undo）。
        return changed ? { ...project, tasks } : project;
      });
    },

    addCalendar: (name, baseCalendarId) => {
      commit('addCalendar', (project) => commands.addCalendar(project, { name, baseCalendarId }).project);
    },

    deleteCalendar: (id) => {
      commit('deleteCalendar', (project) => commands.deleteCalendar(project, id));
    },

    setDefaultCalendar: (id) => {
      commit('setDefaultCalendar', (project) => commands.setDefaultCalendar(project, id));
    },

    updateCalendarWeekDays: (id, weekDays) => {
      commit('updateCalendarWeekDays', (project) => commands.updateCalendarWeekDays(project, id, weekDays));
    },

    updateCalendarExceptions: (id, exceptions) => {
      commit('updateCalendarExceptions', (project) =>
        commands.updateCalendarExceptions(project, id, exceptions),
      );
    },

    createReportPreset: (input) => {
      const state = get();
      if (state.hostPolicy.readOnly) return null;
      const id = input.id?.trim() || nextReportPresetId(state.reportPresets);
      if (state.reportPresets.some((preset) => preset.id === id)) return null;
      const preset = sanitizeReportPreset({ ...input, id });
      if (preset === null || hasDuplicatePresetName(state.reportPresets, preset.name)) return null;
      persistReportPresetState([...state.reportPresets, preset], state.activeReportPresetId ?? preset.id);
      return preset.id;
    },

    updateReportPreset: (id, patch) => {
      const state = get();
      if (state.hostPolicy.readOnly) return;
      const current = state.reportPresets.find((preset) => preset.id === id);
      if (current === undefined) return;
      const next = sanitizeReportPreset({ ...current, ...patch, id: current.id });
      if (next === null || hasDuplicatePresetName(state.reportPresets, next.name, id)) return;
      if (JSON.stringify(next) === JSON.stringify(current)) return;
      persistReportPresetState(
        state.reportPresets.map((preset) => preset.id === id ? next : preset),
        state.activeReportPresetId,
      );
    },

    duplicateReportPreset: (id) => {
      const state = get();
      if (state.hostPolicy.readOnly) return null;
      const source = state.reportPresets.find((preset) => preset.id === id);
      if (source === undefined) return null;
      let suffix = 2;
      let name = `${source.name} 副本`;
      while (hasDuplicatePresetName(state.reportPresets, name)) {
        name = `${source.name} 副本 ${suffix}`;
        suffix += 1;
      }
      const duplicate = { ...source, id: nextReportPresetId(state.reportPresets), name };
      persistReportPresetState([...state.reportPresets, duplicate], duplicate.id);
      return duplicate.id;
    },

    deleteReportPreset: (id) => {
      const state = get();
      if (state.hostPolicy.readOnly) return;
      const presets = state.reportPresets.filter((preset) => preset.id !== id);
      if (presets.length === state.reportPresets.length) return;
      persistReportPresetState(
        presets,
        state.activeReportPresetId === id ? presets[0]?.id ?? null : state.activeReportPresetId,
      );
    },

    selectReportPreset: (id) => {
      const state = get();
      if (id !== null && !state.reportPresets.some((preset) => preset.id === id)) return;
      if (state.activeReportPresetId === id) return;
      if (state.hostPolicy.readOnly) {
        set({ activeReportPresetId: id });
        return;
      }
      persistReportPresetState(state.reportPresets, id);
    },

    restoreReportPresetState: (presets, activeId) => {
      const sanitized = sanitizeReportPresets(presets) ?? cloneDefaultReportPresets();
      const activeReportPresetId =
        typeof activeId === 'string' && sanitized.some((preset) => preset.id === activeId)
          ? activeId
          : sanitized[0]?.id ?? null;
      set({ reportPresets: sanitized, activeReportPresetId });
    },

    setScheduleMode: (mode) => {
      if (get().hostPolicy.readOnly) return;
      set((s) => ({ schedule: { ...s.schedule, mode } }));
      if (mode === 'auto') scheduleAutoRun();
      else clearScheduleTimer();
    },

    runScheduleNow: () => {
      if (get().hostPolicy.readOnly) return;
      clearScheduleTimer();
      applyScheduleResult('manual');
    },

    levelResources: async (options = DEFAULT_LEVELING_OPTIONS) => {
      if (get().hostPolicy.readOnly || get().leveling.isRunning) return;
      clearScheduleTimer();
      // 先同步打開進行中旗標，再把運算讓到下一個 macrotask —— 只有這樣瀏覽器
      // 才有機會在 1～2 秒的同步運算開始前先畫出進行中狀態。
      set({ leveling: { isRunning: true, result: null, projectBefore: null, error: null } });
      await new Promise<void>((resolve) => setTimeout(resolve, 0));

      if (get().hostPolicy.readOnly) {
        set({ leveling: initialLevelingState });
        return;
      }

      const before = get().project;
      const run = runLeveling(before, options);

      if (run.error !== null || run.result === null) {
        set((s) => ({
          leveling: { isRunning: false, result: null, projectBefore: null, error: run.error },
          schedule: { ...s.schedule, diagnostics: run.diagnostics },
        }));
        return;
      }

      const state = get();
      const changed = run.project !== before;
      // 撫平不太可能動到大綱結構，但一律走同一條「改動了 project 就重算
      // WBS」規則，不特別判斷「這次算不算結構性變動」。
      const after = changed ? withWbsCodes(run.project) : before;
      const { history } = changed
        ? commitHistory(state.history, before, after, { label: 'levelResources' })
        : { history: state.history };
      set((s) => ({
        project: after,
        revision: changed ? s.revision + 1 : s.revision,
        history,
        file: changed ? { ...s.file, isDirty: true } : s.file,
        schedule: { ...s.schedule, diagnostics: run.diagnostics },
        leveling: { isRunning: false, result: run.result, projectBefore: before, error: null },
      }));
    },

    dismissLevelingResult: () => {
      set({ leveling: initialLevelingState });
    },

    selectTasks: (taskIds) => set({ selectedTaskIds: taskIds, isTaskDetailsOpen: false, isTaskProgressOpen: false }),
    selectResources: (resourceIds) => set({ selectedResourceIds: resourceIds }),
    setActiveCell: (cell) => set({ activeCell: cell }),

    setActiveView: (view) => set({ activeView: view }),
    /**
     * 相同值一律不寫入——這是「甘特圖回報實際生效刻度」那條路徑的防迴圈保證。
     *
     * 迴路是：下拉選單改 `timeScale` → 甘特圖依新刻度重設 `pxPerDay` →
     * 生效刻度改變 → 回報 → 又呼叫這裡。只要「回報的值等於目前值」時不換
     * state，迴路就在這裡斷掉：state 沒變 → prop 沒變 → 甘特圖不重設
     * `pxPerDay` → 不會再回報。zustand 對相同的 primitive 仍會建立新的
     * state 物件並通知訂閱者，所以這個提早 return 不能省。
     */
    setTimeScale: (scale) => {
      if (get().timeScale === scale) return;
      set({ timeScale: scale });
    },
    setCollapsedTaskIds: (taskIds) => set({ collapsedTaskIds: [...new Set(taskIds)] }),
    toggleCollapsed: (taskIdToToggle) =>
      set((s) => ({
        collapsedTaskIds: s.collapsedTaskIds.includes(taskIdToToggle)
          ? s.collapsedTaskIds.filter((id) => id !== taskIdToToggle)
          : [...s.collapsedTaskIds, taskIdToToggle],
      })),
    setColumns: (columns) => set({ columns }),
    setShowBaseline: (show) => set({ showBaseline: show }),
    setShowCriticalPath: (show) => set({ showCriticalPath: show }),
    setDependencyLineMode: (mode) => {
      writeStoredDependencyLineMode(mode);
      set({ dependencyLineMode: mode });
    },
    setScrollTop: (scrollTop) => set({ scrollTop }),
    openProjectInfo: () => {
      if (get().hostPolicy.readOnly) return;
      set({ ...CLOSED_DIALOGS, isProjectInfoOpen: true });
    },
    closeProjectInfo: () => set({ isProjectInfoOpen: false }),
    openBaselineManager: () => {
      if (get().hostPolicy.readOnly) return;
      set({ ...CLOSED_DIALOGS, isBaselineManagerOpen: true });
    },
    closeBaselineManager: () => set({ isBaselineManagerOpen: false }),
    openTaskDetails: () => {
      // 唯讀宿主（雲端 viewer）也能開：對話框在唯讀下是純檢視，還帶著協作留言與附件。
      if (get().selectedTaskIds.length !== 1) return;
      set({ ...CLOSED_DIALOGS, isTaskDetailsOpen: true });
    },
    closeTaskDetails: () => set({ isTaskDetailsOpen: false }),
    openTaskProgress: () => {
      if (get().hostPolicy.readOnly) return;
      const selectedId = get().selectedTaskIds.length === 1 ? get().selectedTaskIds[0] : undefined;
      const task = selectedId === undefined ? undefined : get().project.tasks.find((candidate) => candidate.id === selectedId);
      if (task === undefined || task.isSummary || !get().project.assignments.some((assignment) => assignment.taskId === task.id)) return;
      set({ ...CLOSED_DIALOGS, isTaskProgressOpen: true });
    },
    closeTaskProgress: () => set({ isTaskProgressOpen: false }),
    openCustomFields: () => {
      if (get().hostPolicy.readOnly) return;
      set({ ...CLOSED_DIALOGS, isCustomFieldsOpen: true });
    },
    closeCustomFields: () => set({ isCustomFieldsOpen: false }),
    openXlsxImport: (workbook) => {
      if (get().hostPolicy.readOnly) return;
      set({ pendingXlsxImport: workbook });
    },
    closeXlsxImport: () => set({ pendingXlsxImport: null }),
    openTemplateExport: () => set({ ...CLOSED_DIALOGS, isTemplateExportOpen: true }),
    closeTemplateExport: () => set({ isTemplateExportOpen: false }),
    openTemplateCreate: (template) => {
      if (get().hostPolicy.readOnly) return;
      set({ ...CLOSED_DIALOGS, pendingTemplate: template });
    },
    closeTemplateCreate: () => set({ pendingTemplate: null }),

    markSaving: (isSaving) => set((s) => ({ file: { ...s.file, isSaving } })),
    markSaved: (filePath, savedRevision) => {
      set((s) => {
        const stillCurrent = savedRevision === undefined || savedRevision === s.revision;
        if (!stillCurrent) {
          // Revision 已經前進，不能斷定是「同一份文件又編輯了一次」還是
          // 「使用者已經切到另一個專案」——不論哪種，`filePath`／
          // `lastSavedAt`／`recentFiles` 都不該被這次（過期的）寫入結果
          // 覆寫：前者會把新編輯誤判成已存檔，後者會把目前文件綁到不屬於
          // 它的存檔路徑上。只把 isSaving 收尾即可，其餘留給下一次成功、
          // 且 revision 仍吻合的存檔去更新。
          return { file: { ...s.file, isSaving: false } };
        }
        return {
          file: {
            ...s.file,
            filePath,
            isDirty: false,
            isSaving: false,
            lastSavedAt: new Date().toISOString(),
            recentFiles: [filePath, ...s.file.recentFiles.filter((f) => f !== filePath)].slice(0, 10),
          },
        };
      });
    },
    markLoading: (isLoading) => set((s) => ({ file: { ...s.file, isLoading } })),
    setDirty: (isDirty) => set((s) => ({ file: { ...s.file, isDirty } })),
  };
});

/** 供測試在計時器情境下確保 debounce 已清除。 */
export function __resetScheduleTimerForTests(): void {
  clearScheduleTimer();
}
